import React, { useState, useEffect } from "react";
import { Drawer, Box, Typography, TextField, Button } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  createSuccessStory,
  updateSuccessStory,
  getSuccessStory,
} from "./successStoriesApi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const AddSuccessStoryDrawer = ({
  projectId,
  isOpen,
  toggle,
  selectedStory,
  onStoryUpdate,
  setRefresh,
}) => {
  const [loading, setLoading] = useState(false);
  const {t} = useTranslation();
  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t("Name is required")),
    description: Yup.string().required(t("Description is required")),
  });

  const initialValues = selectedStory
    ? {
        name: selectedStory.name,
        description: selectedStory.description,
      }
    : {
        name: "",
        description: "",
      };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        if (selectedStory) {
          // Update existing story
          await updateSuccessStory(
            selectedStory.id,
            values.name,
            values.description,
            projectId
          );
          const updatedStoryData = await getSuccessStory(
            selectedStory.id,
            projectId
          );
          onStoryUpdate(updatedStoryData.data);
          toast.success("Success story updated successfully");
          setRefresh((prev) => !prev);
          toggle("success_story_updated");
        } else {
          // Create new story
          const newStory = await createSuccessStory(
            values.name,
            values.description,
            projectId
          );
          console.log("newStory", newStory);
          setRefresh((prev) => !prev);
          toggle("success_story_created");
        }
        toast.success("Success story added successfully");
      } catch (error) {
        console.error("Error creating/updating success story:", error);
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (selectedStory) {
      formik.setValues({
        name: selectedStory.name,
        description: selectedStory.description,
      });
    } else {
      formik.resetForm();
    }
  }, [selectedStory]);

  return (
    <Drawer anchor="right" open={isOpen} onClose={toggle}>
      <Box sx={{ width: 400, padding: 2 }}>
        <Typography variant="h6" gutterBottom>
          {selectedStory ? t("Edit Success Story") : t("Add Success Story")}
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <TextField
            fullWidth
            id="name"
            name="name"
            label={t("Name")}
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            margin="normal"
          />
          <TextField
            fullWidth
            id="description"
            name="description"
            label={t("Description")}
            multiline
            rows={4}
            value={formik.values.description}
            onChange={formik.handleChange}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={formik.touched.description && formik.errors.description}
            margin="normal"
          />
          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={loading}
          >
            {loading
              ? t("Loading...")
              : selectedStory
              ? t("Update Success Story")
              : t("Add Success Story")}
          </Button>
        </form>
      </Box>
    </Drawer>
  );
};

export default AddSuccessStoryDrawer;
